/**
 * Catches a render-time crash and shows it instead of a blank screen.
 *
 * React unmounts the entire tree when a render throws and nothing catches it.
 * In a browser you'd see the console; in a Capacitor WebView you see black,
 * with the real error reachable only over a USB cable. Anything that turns
 * "black screen" into "here is the error" is worth its weight here.
 *
 * This complements the inline watchdog in index.html: that one catches
 * failures BEFORE React mounts (and hangs that never throw at all), this one
 * catches failures after.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { error: Error | null; stack: string | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, stack: info.componentStack ?? null })
    console.error('[lift] render crash:', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="wrap">
        <h1>Something broke</h1>
        <div className="card">
          <p className="muted" style={{ marginTop: 0 }}>
            Nothing you logged is lost — it's stored on this device. Send this
            to whoever maintains the app:
          </p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: 'var(--surface-2)',
              padding: '.75rem',
              borderRadius: 'var(--radius)',
              fontSize: '12px',
              margin: 0,
            }}
          >
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack?.slice(0, 800)}
            {this.state.stack?.slice(0, 500)}
          </pre>
          <button onClick={() => window.location.reload()}>Reload</button>

          {/* Reload alone is a loop when the crash is on the screen you land on,
              which is exactly what happened with 1.0.756375. A plain link needs
              none of the app's state to work, so it survives whatever broke:
              tapping it downloads the current build and installs over the top,
              keeping the local database. This is the only escape hatch that
              still functions when the updater itself is unreachable. */}
          <p className="muted" style={{ marginBottom: '.25rem', fontSize: '.85rem' }}>
            If reloading lands here again, install the current version directly:
          </p>
          <a
            href="https://lift.npcforge.ca/downloads/lift-latest.apk"
            style={{ fontSize: '.9rem' }}
          >
            lift.npcforge.ca/downloads/lift-latest.apk
          </a>
        </div>
      </div>
    )
  }
}
